"use client";

import { renderEmbeddedVideoMarkdownEmbed } from "@/app/(dashboard)/_components/pages/loom-video-embed";
import { pageEditorProseClasses } from "@/app/(dashboard)/_components/shared/markdown-prose-heading-classes";
import { cn } from "@v1/ui/utils";
import Link from "next/link";
import type { AnchorHTMLAttributes, ReactElement, ReactNode } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { HelpHeading } from "./help-headings";
import { useHelpHref } from "./help-link-base";
import { HelpImage } from "./help-image";
import { HelpCallout } from "./help-markdown-blocks";

/** Interne Wikilink-Schemata aus dem Seiten-Markdown (`[Titel](page:<id>)`). */
const PAGE_LINK_SCHEMES = ["page:", "page-archived:", "page-deleted:"] as const;

function pageIdFromHref(href: string): string | null {
  for (const scheme of PAGE_LINK_SCHEMES) {
    if (href.startsWith(scheme)) {
      const rest = href.slice(scheme.length);
      const hashIndex = rest.indexOf("#");
      return hashIndex >= 0 ? rest.slice(0, hashIndex) : rest;
    }
  }
  return null;
}

function isInternalScheme(url: string): boolean {
  return (
    PAGE_LINK_SCHEMES.some((scheme) => url.startsWith(scheme)) ||
    url.startsWith("space:")
  );
}

/** Public help markdown. Wikilinks link only to public targets; others become plain text, private pages stay hidden. */
export function HelpMarkdown({
  content_md,
  org_slug,
  link_targets,
  headings = [],
}: {
  readonly content_md: string;
  readonly org_slug: string;
  readonly link_targets: Readonly<Record<string, { readonly title: string }>>;
  /** Anchors from `extractHelpHeadings(content_md)`, matched by source line. */
  readonly headings?: readonly HelpHeading[];
}): ReactElement {
  const helpHref = useHelpHref(org_slug);
  const anchorByLine = new Map(
    headings.map((heading) => [heading.line, heading.id]),
  );
  const renderAnchor = ({
    node,
    href,
    children,
    ...rest
  }: AnchorHTMLAttributes<HTMLAnchorElement> & {
    readonly node?: unknown;
  }): ReactElement => {
    const url = href ?? "";
    const videoEmbed = renderEmbeddedVideoMarkdownEmbed({
      href,
      className: undefined,
    });
    if (videoEmbed) return videoEmbed;
    const pageId = pageIdFromHref(url);
    if (pageId !== null) {
      if (link_targets[pageId]) {
        return (
          <Link
            href={helpHref({ kind: "page", pageId })}
            className="text-action-700 underline underline-offset-2 hover:text-action-900"
          >
            {children}
          </Link>
        );
      }
      return <span>{children}</span>;
    }
    if (url.startsWith("space:") || url.length === 0) {
      return <span>{children}</span>;
    }
    const isExternal = url.startsWith("http://") || url.startsWith("https://");
    return (
      <a
        {...rest}
        href={url}
        {...(isExternal
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
        className="text-action-700 underline underline-offset-2 hover:text-action-900"
      >
        {children}
      </a>
    );
  };

  // The page's own <h1> lives in the article header, so downshift every authored
  // heading by one level to keep a single h1 and a monotonic outline (a11y + SEO).
  const heading =
    (Tag: "h2" | "h3" | "h4" | "h5" | "h6") =>
    ({
      node,
      children,
    }: {
      readonly node?: {
        readonly position?: { readonly start: { readonly line: number } };
      };
      readonly children?: ReactNode;
    }): ReactElement => {
      const line = node?.position?.start.line;
      const id = line === undefined ? undefined : anchorByLine.get(line);
      return (
        <Tag id={id} className={id ? "scroll-mt-[81px]" : undefined}>
          {children}
        </Tag>
      );
    };

  return (
    <div
      className={cn(
        pageEditorProseClasses,
        // Article rhythm like public docs: 15px body, air between paragraphs.
        "text-[15px] leading-[1.7] text-primary/80",
        "[&_p]:mb-4 [&_ul]:mb-4 [&_ol]:mb-4 [&_li]:mt-1",
        // Authored `##`/`###` render one level down (h3/h4): give them a clear section weight.
        "[&_h3]:mb-2 [&_h3]:mt-9 [&_h3]:text-[19px] [&_h3]:font-semibold [&_h3]:text-primary/90",
        "[&_h4]:mb-1.5 [&_h4]:mt-6 [&_h4]:text-[15px] [&_h4]:font-semibold [&_h4]:text-primary/85",
        "[&_h5]:mb-1 [&_h5]:mt-4 [&_h5]:text-[14px] [&_h5]:font-semibold [&_h5]:text-primary/80",
        "select-text",
        "[&_table]:my-5 [&_table]:w-full [&_table]:text-[14px] [&_th]:border [&_td]:border [&_th]:bg-primary/[0.03] [&_th]:px-3 [&_td]:px-3 [&_th]:py-1.5 [&_td]:py-1.5 [&_th]:text-left [&_th]:font-semibold",
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={(url) =>
          isInternalScheme(url) ? url : defaultUrlTransform(url)
        }
        components={{
          a: renderAnchor,
          img: HelpImage,
          blockquote: HelpCallout,
          h1: heading("h2"),
          h2: heading("h3"),
          h3: heading("h4"),
          h4: heading("h5"),
          h5: heading("h6"),
          h6: heading("h6"),
        }}
      >
        {content_md}
      </ReactMarkdown>
    </div>
  );
}
